import { useMemo, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { Breadcrumb } from "@/components/layout/Breadcrumb";
import {
  PlayCircle, MessageSquare, Brain, Zap, Cpu, Database, Layers,
  AlertTriangle, CheckCircle2, XCircle, Clock, Coins, Search,
} from "lucide-react";
import {
  useReplayGroups, useReplayMessages, useMessageTraces,
  type ReplayMessage, type MessageTrace,
} from "@/hooks/useConversationReplay";

function isoMinusHours(h: number) {
  return new Date(Date.now() - h * 3_600_000).toISOString().slice(0, 16);
}

export default function ConversationReplay() {
  const { data: groups = [], isLoading: loadingGroups } = useReplayGroups();
  const [groupId, setGroupId] = useState<string | null>(null);
  const [selectionMode, setSelectionMode] = useState<"window" | "list">("window");

  // window mode
  const [from, setFrom] = useState(() => isoMinusHours(2));
  const [to, setTo] = useState(() => new Date().toISOString().slice(0, 16));

  // list mode
  const [search, setSearch] = useState("");
  const [picked, setPicked] = useState<Set<string>>(new Set());

  // run state
  const [runMessages, setRunMessages] = useState<ReplayMessage[]>([]);

  const { data: rawMessages = [], isLoading: loadingMsgs } = useReplayMessages({
    group_id: groupId,
    mode: selectionMode,
    from: selectionMode === "window" ? new Date(from).toISOString() : undefined,
    to: selectionMode === "window" ? new Date(to).toISOString() : undefined,
    limit: selectionMode === "window" ? 200 : 100,
  });

  const filteredList = useMemo(() => {
    if (!search) return rawMessages;
    const q = search.toLowerCase();
    return rawMessages.filter(
      (m) =>
        m.content?.toLowerCase().includes(q) || m.sender_name?.toLowerCase().includes(q)
    );
  }, [rawMessages, search]);

  const togglePick = (id: string) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const startReplay = () => {
    if (selectionMode === "window") {
      setRunMessages([...rawMessages].reverse());
    } else {
      setRunMessages(rawMessages.filter((m) => picked.has(m.id)).reverse());
    }
  };

  const { data: traces = [], isLoading: loadingTraces } = useMessageTraces(runMessages);

  const summary = useMemo(() => {
    const totalCost = traces.reduce(
      (acc, t) => acc + t.modelDecisions.reduce((a, d) => a + (Number(d.estimated_cost_usd) || 0), 0),
      0
    );
    const totalLatency = traces.reduce(
      (acc, t) => acc + t.modelDecisions.reduce((a, d) => a + (d.latency_ms || 0), 0),
      0
    );
    const failures = traces.reduce(
      (acc, t) => acc + t.modelDecisions.filter((d) => d.success === false).length,
      0
    );
    const skillsRun = traces.reduce(
      (acc, t) => acc + t.skillsLegacy.length + t.skillsV19.length,
      0
    );
    const metacogRejects = traces.reduce(
      (acc, t) => acc + t.metacog.filter((m) => m.decision === "reject" || m.decision === "abort").length,
      0
    );
    return { totalCost, totalLatency, failures, skillsRun, metacogRejects };
  }, [traces]);

  return (
    <div className="space-y-6">
      <Breadcrumb items={[{ label: "Replay" }, { label: "Conversa" }]} />

      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-foreground font-headline">Conversation Replay</h1>
          <p className="text-muted-foreground text-sm">
            Trace passivo: persona, classificação, ToM, metacog, decisões de modelo e execuções de skills (legacy + v19) para mensagens já processadas.
          </p>
        </div>
      </div>

      {/* Selector */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">1. Selecione o grupo e as mensagens</CardTitle>
          <CardDescription>Sem custo de IA — só leitura dos eventos já gravados.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>Grupo</Label>
              <Select value={groupId ?? ""} onValueChange={(v) => { setGroupId(v); setPicked(new Set()); }}>
                <SelectTrigger>
                  <SelectValue placeholder={loadingGroups ? "Carregando..." : "Escolha um grupo"} />
                </SelectTrigger>
                <SelectContent>
                  {groups.map((g) => (
                    <SelectItem key={g.id} value={g.id}>
                      {g.name} {g.sandbox_mode && <Badge variant="outline" className="ml-1 text-[10px]">sandbox</Badge>}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <Tabs value={selectionMode} onValueChange={(v) => setSelectionMode(v as typeof selectionMode)}>
            <TabsList>
              <TabsTrigger value="window">Por janela</TabsTrigger>
              <TabsTrigger value="list">Por mensagem</TabsTrigger>
            </TabsList>

            <TabsContent value="window" className="space-y-3 pt-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label>De</Label>
                  <Input type="datetime-local" value={from} onChange={(e) => setFrom(e.target.value)} />
                </div>
                <div>
                  <Label>Até</Label>
                  <Input type="datetime-local" value={to} onChange={(e) => setTo(e.target.value)} />
                </div>
              </div>
              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground">
                  {loadingMsgs ? "carregando..." : `${rawMessages.length} mensagens encontradas`}
                </p>
                <Button onClick={startReplay} disabled={!groupId || rawMessages.length === 0}>
                  <PlayCircle className="w-4 h-4 mr-1" /> Reenviar janela ({rawMessages.length})
                </Button>
              </div>
            </TabsContent>

            <TabsContent value="list" className="space-y-3 pt-3">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-2 top-2.5 text-muted-foreground" />
                <Input
                  className="pl-8"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="filtrar por conteúdo ou remetente..."
                />
              </div>
              <ScrollArea className="h-72 rounded border border-border p-2">
                {loadingMsgs ? (
                  <Skeleton className="h-60" />
                ) : filteredList.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-8">
                    Nenhuma mensagem encontrada para esse grupo.
                  </p>
                ) : (
                  <div className="space-y-1">
                    {filteredList.map((m) => (
                      <label
                        key={m.id}
                        className="flex gap-2 items-start p-2 rounded hover:bg-muted/40 cursor-pointer"
                      >
                        <Checkbox
                          checked={picked.has(m.id)}
                          onCheckedChange={() => togglePick(m.id)}
                          className="mt-1"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <span className="font-medium text-foreground">{m.sender_name ?? "—"}</span>
                            <span>{new Date(m.created_at).toLocaleString("pt-BR")}</span>
                            <Badge variant="outline" className="text-[10px]">{m.message_type ?? "text"}</Badge>
                          </div>
                          <p className="text-sm truncate">{m.content || <em className="text-muted-foreground">(sem texto)</em>}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                )}
              </ScrollArea>
              <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground">{picked.size} selecionada(s)</p>
                <Button onClick={startReplay} disabled={picked.size === 0}>
                  <PlayCircle className="w-4 h-4 mr-1" /> Reenviar selecionadas ({picked.size})
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>

      {/* Summary */}
      {runMessages.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <SummaryCard label="Mensagens" value={runMessages.length} icon={<MessageSquare className="w-4 h-4" />} />
          <SummaryCard label="Skills exec" value={summary.skillsRun} icon={<Zap className="w-4 h-4" />} />
          <SummaryCard label="Falhas modelo" value={summary.failures} icon={<XCircle className="w-4 h-4 text-destructive" />} />
          <SummaryCard label="Metacog rejeições" value={summary.metacogRejects} icon={<AlertTriangle className="w-4 h-4 text-warning" />} />
          <SummaryCard
            label="Custo total"
            value={`$${summary.totalCost.toFixed(4)}`}
            sub={`${summary.totalLatency}ms`}
            icon={<Coins className="w-4 h-4" />}
          />
        </div>
      )}

      {/* Traces */}
      {runMessages.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">2. Trace por mensagem</CardTitle>
            <CardDescription>Ordem cronológica (mais antiga primeiro). Expanda cada mensagem para ver o trace completo.</CardDescription>
          </CardHeader>
          <CardContent>
            {loadingTraces ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => <Skeleton key={i} className="h-32" />)}
              </div>
            ) : (
              <Accordion type="multiple" className="space-y-2">
                {traces.map((t, i) => (
                  <TraceAccordionItem key={t.message.id} trace={t} index={i + 1} />
                ))}
              </Accordion>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function SummaryCard({
  label, value, sub, icon,
}: {
  label: string;
  value: string | number;
  sub?: string;
  icon: React.ReactNode;
}) {
  return (
    <Card>
      <CardContent className="p-3">
        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
          {icon} {label}
        </div>
        <div className="text-xl font-semibold text-foreground">{value}</div>
        {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
      </CardContent>
    </Card>
  );
}

function TraceAccordionItem({ trace, index }: { trace: MessageTrace; index: number }) {
  const m = trace.message;
  const topIntent = trace.intents[0];
  const tom = trace.tom[0];
  const mc = trace.metacog[0];
  const dec = trace.modelDecisions[0];
  const totalSkills = trace.skillsLegacy.length + trace.skillsV19.length;

  return (
    <AccordionItem value={m.id} className="border rounded-md">
      <AccordionTrigger className="px-3 hover:no-underline">
        <div className="flex items-start gap-3 w-full text-left">
          <Badge variant="outline" className="shrink-0">#{index}</Badge>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-0.5">
              <span className="font-medium text-foreground">{m.sender_name ?? "—"}</span>
              <Clock className="w-3 h-3" />
              <span>{new Date(m.created_at).toLocaleString("pt-BR")}</span>
            </div>
            <p className="text-sm line-clamp-1">{m.content || <em>(sem texto)</em>}</p>
          </div>
          <div className="flex gap-1 shrink-0">
            {topIntent && (
              <Badge variant="secondary" className="text-[10px]">
                {topIntent.intent} {Math.round(Number(topIntent.confidence) * 100)}%
              </Badge>
            )}
            {totalSkills > 0 && <Badge variant="outline" className="text-[10px]">{totalSkills} skills</Badge>}
            {dec?.success === false && <Badge variant="destructive" className="text-[10px]">err</Badge>}
            {mc?.decision === "reject" && <Badge variant="destructive" className="text-[10px]">metacog</Badge>}
          </div>
        </div>
      </AccordionTrigger>
      <AccordionContent className="px-3 pb-3 space-y-3">
        {/* Full message */}
        <Section icon={<MessageSquare className="w-3 h-3" />} title="Mensagem original">
          <div className="text-xs text-muted-foreground mb-1">
            {m.sender_phone} · {m.message_type ?? "text"} · id: <code>{m.id}</code>
          </div>
          <div className="text-sm whitespace-pre-wrap rounded bg-muted/40 p-2">
            {m.content || <em className="text-muted-foreground">(sem texto — mídia)</em>}
          </div>
        </Section>

        {/* Persona */}
        <Section icon={<Cpu className="w-3 h-3" />} title="Persona ativa">
          {trace.persona ? (
            <div className="space-y-1">
              <div className="flex flex-wrap gap-1.5 text-xs">
                <Badge variant="outline">v{trace.persona.persona_base_version ?? "?"}</Badge>
                {trace.persona.cultural_register && <Badge variant="secondary">{trace.persona.cultural_register}</Badge>}
                <Badge variant="outline">{trace.persona.source ?? "—"}</Badge>
              </div>
              <JsonBlock value={trace.persona.persona_base} />
              {trace.persona.enabled_skills && trace.persona.enabled_skills.length > 0 && (
                <div className="text-xs text-muted-foreground">
                  Skills habilitadas: {trace.persona.enabled_skills.join(", ")}
                </div>
              )}
            </div>
          ) : (
            <Empty>Sem snapshot de persona anterior à mensagem.</Empty>
          )}
        </Section>

        {/* Intents */}
        <Section icon={<Brain className="w-3 h-3" />} title={`Classificação de intent (${trace.intents.length})`}>
          {trace.intents.length === 0 ? (
            <Empty>Nenhuma intent registrada.</Empty>
          ) : (
            <div className="space-y-1">
              {trace.intents.map((i) => (
                <div key={i.id} className="flex items-center gap-2 text-xs">
                  <Badge variant="secondary">{i.intent}</Badge>
                  <span className="text-muted-foreground">{Math.round(Number(i.confidence) * 100)}%</span>
                  {i.was_correct === true && <CheckCircle2 className="w-3 h-3 text-primary" />}
                  {i.was_correct === false && (
                    <Badge variant="destructive" className="text-[10px]">corrigida → {i.corrected_intent}</Badge>
                  )}
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* ToM */}
        <Section icon={<Brain className="w-3 h-3" />} title="ToM (theory of mind do turno)">
          {trace.tom.length === 0 ? (
            <Empty>Sem inferências ToM.</Empty>
          ) : (
            trace.tom.map((t) => (
              <div key={t.id} className="space-y-1">
                <div className="flex flex-wrap gap-1.5 text-xs">
                  {t.primary_intent && <Badge variant="secondary">{t.primary_intent}</Badge>}
                  {t.urgency != null && <Badge variant="outline">urgency {Number(t.urgency).toFixed(2)}</Badge>}
                  {t.vulnerability != null && <Badge variant="outline">vuln {Number(t.vulnerability).toFixed(2)}</Badge>}
                  {t.openness_to_challenge != null && <Badge variant="outline">openness {Number(t.openness_to_challenge).toFixed(2)}</Badge>}
                  {t.confidence_parsing != null && <Badge variant="outline">conf {Number(t.confidence_parsing).toFixed(2)}</Badge>}
                </div>
                {t.turn_affect != null && <JsonBlock value={t.turn_affect} label="turn_affect" />}
                {t.raw_signals != null && <JsonBlock value={t.raw_signals} label="raw_signals" />}
              </div>
            ))
          )}
        </Section>

        {/* Affect */}
        {trace.affect && (
          <Section icon={<Layers className="w-3 h-3" />} title="Affect state (janela)">
            <div className="flex flex-wrap gap-1.5 text-xs">
              <Badge variant="outline">valence {Number(trace.affect.valence).toFixed(2)}</Badge>
              <Badge variant="outline">arousal {Number(trace.affect.arousal).toFixed(2)}</Badge>
              <Badge variant="outline">dominance {Number(trace.affect.dominance).toFixed(2)}</Badge>
            </div>
            <JsonBlock value={trace.affect.signals} label="signals" />
          </Section>
        )}

        {/* Metacog */}
        <Section icon={<AlertTriangle className="w-3 h-3" />} title={`Metacog gate (${trace.metacog.length})`}>
          {trace.metacog.length === 0 ? (
            <Empty>Não passou pelo gate.</Empty>
          ) : (
            trace.metacog.map((m) => (
              <div key={m.id} className="space-y-1">
                <div className="flex flex-wrap gap-1.5 text-xs">
                  <Badge variant={m.decision === "accept" ? "default" : "destructive"}>{m.decision}</Badge>
                  {m.task_class && <Badge variant="outline">{m.task_class}</Badge>}
                  {m.pre_response_confidence != null && (
                    <Badge variant="outline">conf {Number(m.pre_response_confidence).toFixed(2)}</Badge>
                  )}
                  <Badge variant={m.applied ? "secondary" : "outline"}>{m.applied ? "aplicado" : "shadow"}</Badge>
                </div>
                {m.reason && <div className="text-xs text-muted-foreground italic">{m.reason}</div>}
                <JsonBlock value={m.signals} label="signals" />
                <JsonBlock value={m.thresholds} label="thresholds" />
              </div>
            ))
          )}
        </Section>

        {/* Model decisions */}
        <Section icon={<Cpu className="w-3 h-3" />} title={`Decisões de modelo (${trace.modelDecisions.length})`}>
          {trace.modelDecisions.length === 0 ? (
            <Empty>Sem chamadas ao modelo nessa janela.</Empty>
          ) : (
            trace.modelDecisions.map((d) => (
              <div key={d.id} className="border rounded p-2 space-y-1">
                <div className="flex flex-wrap gap-1.5 text-xs">
                  <Badge variant="secondary">{d.provider_used}/{d.model_used}</Badge>
                  {d.task_class && <Badge variant="outline">{d.task_class}</Badge>}
                  {d.skill_name && <Badge variant="outline">skill: {d.skill_name}</Badge>}
                  {d.was_fallback && <Badge variant="destructive" className="text-[10px]">fallback</Badge>}
                  {d.verifier_rejected && <Badge variant="destructive" className="text-[10px]">verifier rejected</Badge>}
                  {d.success === false && <Badge variant="destructive" className="text-[10px]">erro</Badge>}
                </div>
                <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                  <span>{d.latency_ms}ms</span>
                  <span>{d.input_tokens}↓ / {d.output_tokens}↑ tokens</span>
                  <span>${Number(d.estimated_cost_usd ?? 0).toFixed(5)}</span>
                  {d.error_type && <span className="text-destructive">{d.error_type}</span>}
                </div>
                {d.reason && <div className="text-xs italic text-muted-foreground">{d.reason}</div>}
                {d.alternatives_considered != null && <JsonBlock value={d.alternatives_considered} label="alternatives" />}
              </div>
            ))
          )}
        </Section>

        {/* Context snapshots */}
        {trace.contextSnapshots.length > 0 && (
          <Section icon={<Database className="w-3 h-3" />} title={`Context snapshots (${trace.contextSnapshots.length})`}>
            {trace.contextSnapshots.map((c) => (
              <div key={c.id} className="border rounded p-2 space-y-1">
                <div className="flex flex-wrap gap-1.5 text-xs">
                  {c.context_window_pct != null && <Badge variant="outline">{Math.round(Number(c.context_window_pct))}% ctx</Badge>}
                  {c.total_tokens_used != null && <Badge variant="outline">{c.total_tokens_used} tokens</Badge>}
                </div>
                {c.sources_included && <div className="text-xs text-muted-foreground">incluído: {c.sources_included.join(", ")}</div>}
                {c.sources_skipped && c.sources_skipped.length > 0 && (
                  <div className="text-xs text-muted-foreground">pulado: {c.sources_skipped.join(", ")}</div>
                )}
                <JsonBlock value={c.context_sources} label="context_sources" />
              </div>
            ))}
          </Section>
        )}

        {/* Skills v19 */}
        <Section icon={<Zap className="w-3 h-3" />} title={`Skills v19 (${trace.skillsV19.length})`}>
          {trace.skillsV19.length === 0 ? (
            <Empty>Nenhuma skill v19 acionada.</Empty>
          ) : (
            trace.skillsV19.map((s) => (
              <div key={s.id} className="border rounded p-2 space-y-1">
                <div className="flex flex-wrap gap-1.5 text-xs">
                  <Badge variant="secondary">{s.skill_slug} v{s.skill_version}</Badge>
                  <Badge variant={s.status === "success" ? "default" : "outline"}>{s.status}</Badge>
                  {s.shadow_mode && <Badge variant="outline">shadow</Badge>}
                  {s.matched_intent && <Badge variant="outline">intent: {s.matched_intent}</Badge>}
                  {s.match_score != null && <Badge variant="outline">score {Number(s.match_score).toFixed(2)}</Badge>}
                </div>
                <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                  {s.latency_ms != null && <span>{s.latency_ms}ms</span>}
                  {s.cost_usd != null && <span>${Number(s.cost_usd).toFixed(5)}</span>}
                  {s.tools_used && s.tools_used.length > 0 && <span>tools: {s.tools_used.join(",")}</span>}
                  {s.user_reaction && <span>reaction: {s.user_reaction}</span>}
                </div>
                {s.error_message && <div className="text-xs text-destructive">{s.error_message}</div>}
                <JsonBlock value={s.pre_check} label="pre_check" />
                <JsonBlock value={s.post_check} label="post_check" />
                <JsonBlock value={s.metacog_decision} label="metacog_decision" />
              </div>
            ))
          )}
        </Section>

        {/* Skills legacy */}
        {trace.skillsLegacy.length > 0 && (
          <Section icon={<Zap className="w-3 h-3" />} title={`Skills legacy (${trace.skillsLegacy.length})`}>
            {trace.skillsLegacy.map((s) => (
              <div key={s.id} className="border rounded p-2 space-y-1">
                <div className="flex flex-wrap gap-1.5 text-xs">
                  <Badge variant="secondary">{s.skill_name}</Badge>
                  {s.task_class && <Badge variant="outline">{s.task_class}</Badge>}
                  {s.metacog_decision && <Badge variant="outline">metacog: {s.metacog_decision}</Badge>}
                  {s.rollout_state_at_exec && <Badge variant="outline">{s.rollout_state_at_exec}</Badge>}
                  {s.was_sent_to_group ? (
                    <Badge variant="default" className="text-[10px]">enviado</Badge>
                  ) : (
                    <Badge variant="outline" className="text-[10px]">não enviado</Badge>
                  )}
                  {s.is_test && <Badge variant="outline">test</Badge>}
                </div>
                <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                  {s.execution_time_ms != null && <span>{s.execution_time_ms}ms</span>}
                  {s.match_score != null && <span>match {Number(s.match_score).toFixed(2)}</span>}
                  {s.self_report_score != null && <span>self {Number(s.self_report_score).toFixed(2)}</span>}
                  {s.output_length != null && <span>{s.output_length} chars</span>}
                </div>
                <JsonBlock value={s.context_metadata} label="context_metadata" />
              </div>
            ))}
          </Section>
        )}

        {/* Working memory */}
        {trace.workingMemory != null && (
          <Section icon={<Database className="w-3 h-3" />} title="Working memory (último estado conhecido)">
            <JsonBlock value={trace.workingMemory} />
          </Section>
        )}
      </AccordionContent>
    </AccordionItem>
  );
}

function Section({
  icon, title, children,
}: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <div className="border-l-2 border-border pl-3">
      <div className="flex items-center gap-1.5 text-xs font-semibold text-foreground mb-1">
        {icon} {title}
      </div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p className="text-xs text-muted-foreground italic">{children}</p>;
}

function JsonBlock({ value, label }: { value: unknown; label?: string }) {
  if (value == null) return null;
  const isEmpty =
    (typeof value === "object" && value !== null && Object.keys(value as object).length === 0) ||
    (Array.isArray(value) && value.length === 0);
  if (isEmpty) return null;
  return (
    <details className="text-xs">
      <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
        {label ?? "JSON"}
      </summary>
      <pre className="mt-1 p-2 rounded bg-muted/40 overflow-x-auto max-h-60 text-[11px]">
        {JSON.stringify(value, null, 2)}
      </pre>
    </details>
  );
}
